import { useState, useRef, useEffect } from "react";
import { CryptoIcon } from "./CriptoIcon";

interface CryptoSelectProps {
    options: { id: string | number; coin: string; description?: string; chain?: string }[];
    value?: string | number;
    onChange: (value: string) => void;
    placeholder?: string;
}

const CryptoSelect = ({ options, value, onChange, placeholder = "Select cryptocurrency" }: CryptoSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const selected = options.find((o) => String(o.id) === String(value)) || options[0];

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={ref} className="relative w-full">
            {/* Trigger */}
            <button
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                className="w-full flex items-center gap-3 text-white text-sm font-medium px-4 py-3.5 rounded-xl cursor-pointer focus:outline-none transition-all duration-200"
                style={{
                    background: "rgba(255, 255, 255, 0.06)",
                    border: `1px solid ${isOpen ? "rgba(77, 189, 255, 0.4)" : "rgba(255, 255, 255, 0.1)"}`,
                }}
            >
                {selected ? (
                    <>
                        <CryptoIcon name={selected.coin} size="20px" variant="color" />
                        <span className="flex-1 text-left">
                            {selected.coin}
                            {selected.description && (
                                <span className="text-white/40 ml-1">({selected.description})</span>
                            )}
                        </span>
                    </>
                ) : (
                    <span className="flex-1 text-left text-white/40">{placeholder}</span>
                )}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4DBDFF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div
                    className="absolute z-50 mt-1.5 w-full rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto"
                    style={{
                        background: "#141e30",
                        border: "1px solid rgba(77, 189, 255, 0.2)",
                    }}
                >
                    {options.map((option) => {
                        const isSelected = String(option.id) === String(selected?.id);
                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                    onChange(String(option.id));
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 text-left ${
                                    isSelected
                                        ? "bg-[#4DBDFF]/10 text-white"
                                        : "text-white/80 hover:bg-white/[0.06]"
                                }`}
                            >
                                <CryptoIcon name={option.coin} size="20px" variant="color" />
                                <span className="flex-1">
                                    {option.coin}
                                    {option.description && (
                                        <span className="text-white/40 ml-1">({option.description})</span>
                                    )}
                                </span>
                                {option.chain && (
                                    <span className="text-white/30 text-xs">{option.chain}</span>
                                )}
                                {isSelected && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4DBDFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CryptoSelect;
